import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Loader2, Save, KeyRound, ShieldCheck, FlaskConical, Radio, Plug, Info } from "lucide-react";
import { platformApi } from "../../lib/api";
import { formatApiErrorDetail } from "../../contexts/AuthContext";

const inputClass =
  "w-full h-9 px-2.5 rounded-md border border-white/10 bg-white/5 text-[13px] text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-400/40 transition-colors";

/** Platform-level Stripe connection — super-admin only. Bills signed-up companies. */
export default function PlatformStripeCard() {
  const [s, setS] = useState(null);
  const [testSecret, setTestSecret] = useState("");
  const [liveSecret, setLiveSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [dirty, setDirty] = useState(false);

  const reload = async () => {
    try {
      const cfg = await platformApi.stripe.getSettings();
      setS(cfg);
      setDirty(false);
      setTestSecret("");
      setLiveSecret("");
    } catch (err) {
      console.error(err);
      toast.error("Failed to load platform Stripe settings");
    }
  };
  useEffect(() => {
    let active = true;
    (async () => { await Promise.resolve(); if (active) reload(); })();
    return () => { active = false; };
  }, []);

  const mark = (patch) => {
    setS((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        mode: s.mode || "test",
        testPublishableKey: s.testPublishableKey || "",
        livePublishableKey: s.livePublishableKey || "",
        ...(testSecret ? { testSecretKey: testSecret } : {}),
        ...(liveSecret ? { liveSecretKey: liveSecret } : {}),
      };
      const updated = await platformApi.stripe.updateSettings(payload);
      setS(updated);
      setTestSecret("");
      setLiveSecret("");
      setDirty(false);
      toast.success("Platform Stripe settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    if (dirty) {
      toast.error("Save your keys before testing the connection.");
      return;
    }
    setTesting(true);
    try {
      const res = await platformApi.stripe.testConnection();
      toast.success(
        `Connected to Stripe in ${res.mode} mode${res.livemode ? " (live)" : ""}${res.usedEnvFallback ? " · using managed key" : ""}`
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Stripe connection failed");
    } finally {
      setTesting(false);
    }
  };

  if (!s) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-6 text-center text-slate-400" data-testid="platform-stripe-card">
        <Loader2 className="h-4 w-4 inline animate-spin" />
      </div>
    );
  }

  const mode = s.mode || "test";

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-6 max-w-3xl" data-testid="platform-stripe-card">
      <div className="flex items-center gap-2 mb-1">
        <CreditCard className="h-4 w-4 text-cyan-300" />
        <h2 className="text-[15px] font-semibold text-slate-100">Platform Stripe account</h2>
        <span
          data-testid="platform-stripe-status"
          className={`ml-1 text-[10.5px] font-semibold px-1.5 py-0.5 rounded border ${
            s.configured
              ? "text-emerald-300 bg-emerald-500/10 border-emerald-500/30"
              : "text-amber-300 bg-amber-500/10 border-amber-500/30"
          }`}
        >
          {s.configured ? `Connected · ${mode}` : "Not connected"}
        </span>
      </div>
      <p className="text-[12.5px] text-slate-400 mb-3 leading-relaxed">
        Connect the Stripe account ServiceGuru Pro uses to bill the companies that sign up — subscription checkout and
        platform invoices are charged here. Secret keys are stored encrypted and never shown again. Find your keys at{" "}
        <span className="font-medium text-slate-300">dashboard.stripe.com → Developers → API keys</span>.
      </p>

      {!s.configured && s.envFallback && (
        <div className="flex items-start gap-2 rounded-lg border border-cyan-500/20 bg-cyan-500/[0.06] px-3 py-2 mb-4 text-[12px] text-cyan-200" data-testid="platform-stripe-envnote">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>A managed Stripe key is currently active as a fallback. Paste your own keys below to bill through your own Stripe account.</span>
        </div>
      )}

      {/* Mode toggle */}
      <div className="mb-4">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 mb-2">Active mode</div>
        <div className="grid grid-cols-2 gap-2 max-w-xs">
          {[
            { key: "test", label: "Test", icon: FlaskConical },
            { key: "live", label: "Live", icon: Radio },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => mark({ mode: key })}
              data-testid={`platform-stripe-mode-${key}`}
              className={`h-9 rounded-lg border text-[13px] font-medium flex items-center justify-center gap-1.5 transition-colors ${
                mode === key
                  ? key === "live"
                    ? "border-rose-400/40 bg-rose-500/10 text-rose-300"
                    : "border-cyan-400/40 bg-cyan-500/10 text-cyan-200"
                  : "border-white/10 bg-white/5 text-slate-400 hover:bg-white/10"
              }`}
            >
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>
        {mode === "live" && (
          <p className="text-[11px] text-rose-300 mt-1.5">Live mode charges real cards. Make sure your live keys are correct.</p>
        )}
      </div>

      <KeyBlock
        title="Test keys"
        pubValue={s.testPublishableKey || ""}
        onPub={(v) => mark({ testPublishableKey: v })}
        pubTestId="platform-stripe-test-publishable"
        secretValue={testSecret}
        onSecret={(v) => { setTestSecret(v); setDirty(true); }}
        secretTestId="platform-stripe-test-secret"
        secretConfigured={s.testSecretConfigured}
        pubPlaceholder="pk_test_…"
        secretPlaceholder="sk_test_…"
      />
      <KeyBlock
        title="Live keys"
        pubValue={s.livePublishableKey || ""}
        onPub={(v) => mark({ livePublishableKey: v })}
        pubTestId="platform-stripe-live-publishable"
        secretValue={liveSecret}
        onSecret={(v) => { setLiveSecret(v); setDirty(true); }}
        secretTestId="platform-stripe-live-secret"
        secretConfigured={s.liveSecretConfigured}
        pubPlaceholder="pk_live_…"
        secretPlaceholder="sk_live_…"
      />

      <div className="flex items-center justify-between mt-2">
        <button
          type="button"
          onClick={testConnection}
          disabled={testing || dirty || (!s.configured && !s.envFallback)}
          data-testid="platform-stripe-test-btn"
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-[13px] font-semibold text-slate-200 disabled:opacity-40"
        >
          {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}
          Test connection
        </button>
        <button
          onClick={save}
          disabled={!dirty || saving}
          data-testid="platform-stripe-save-btn"
          className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 disabled:opacity-40 text-white text-[13px] font-semibold"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save Stripe settings
        </button>
      </div>
    </div>
  );
}

function KeyBlock({ title, pubValue, onPub, pubTestId, secretValue, onSecret, secretTestId, secretConfigured, pubPlaceholder, secretPlaceholder }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] p-4 mb-3">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-400 mb-3">{title}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-[11px] font-semibold text-slate-400 mb-1">Publishable key</label>
          <div className="relative">
            <ShieldCheck className="h-3.5 w-3.5 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input value={pubValue} onChange={(e) => onPub(e.target.value)} data-testid={pubTestId} placeholder={pubPlaceholder} className={`${inputClass} pl-8`} />
          </div>
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-slate-400 mb-1">Secret key</label>
          <div className="relative">
            <KeyRound className="h-3.5 w-3.5 text-slate-500 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input type="password" value={secretValue} onChange={(e) => onSecret(e.target.value)} data-testid={secretTestId} placeholder={secretConfigured ? "•••••••• (saved — leave blank to keep)" : secretPlaceholder} className={`${inputClass} pl-8`} />
          </div>
        </div>
      </div>
    </div>
  );
}
